import { Dispatch, useState, SetStateAction, useEffect } from "react";
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import Sidebar from "../../Components/Sidebar/Sidebar";
import SidebarGroup from "../../Components/Sidebar/SidebarGroup";
import { SaleQuery } from "../../system/Domain";
import useSalesChannels from "../../system/useSalesChannels";
import SidebarButton from "Components/Sidebar/SidebarButton";
import { GetApp } from "@mui/icons-material";
import axios from "axios";
import parseContentDispositionFilename from "system/parseContentDispositionFilename";
import fileDownload from "js-file-download";
import showMessage from "Dialogs/showMessage";
import SidebarForm from "Components/Sidebar/SidebarForm";

export interface Props {
	query: SaleQuery;
	setQuery: Dispatch<SetStateAction<SaleQuery>>;
}

const SalesTableSidebar = ({ query, setQuery }: Props) => {
	const [salesChannels] = useSalesChannels();
	const [channel, setChannel] = useState("-");
	const [perspective, setPerspective] = useState<string>("All");

	const defaultPerspective = {
		isStopped: null, statuses: null, isExport: null, isConfirmedExport: null, isReclassifiedExport: null
	};

	const statusPerspectives = {
		All: { ...defaultPerspective, name: "Alle" },
		Active: { ...defaultPerspective, name: "Aktiv", statuses: ["Returned", "ReadyToAllocate", "ReadyToSell"] },
		Reserved: { ...defaultPerspective, name: "Reserviert", statuses: ["Reserved"] },
		Sold: { ...defaultPerspective, name: "Verkauft", statuses: ["Sold"] },
		Paid: { ...defaultPerspective, name: "Bezahlt", statuses: ["ReadyForShipping"] },
		Closed: { ...defaultPerspective, name: "Abgeschlossen", statuses: ["Shipped", "End"] },
		Export: { ...defaultPerspective, name: "Offene Exporte", isExport: true },
		ConfirmedExports: { ...defaultPerspective, name: "Bestätigte Exporte", isConfirmedExport: true },
		ReclassifiedExports: {...defaultPerspective, name: "Umfaktorierte Exporte", isReclassifiedExport: true},
		Cancelled: { ...defaultPerspective, name: "Abgebrochen", statuses: ["Cancelled"] },
		Stopped: { ...defaultPerspective, name: "Gestoppt", isStopped: true },
	};

	const updateQuery = (query: SaleQuery, perspective: string, channelName: string): SaleQuery => ({
		...query,
		status: statusPerspectives[perspective].statuses,
		isStopped: statusPerspectives[perspective].isStopped,
		isExport: statusPerspectives[perspective].isExport,
		isConfirmedExport: statusPerspectives[perspective].isConfirmedExport,
		isReclassifiedExport: statusPerspectives[perspective].isReclassifiedExport,
		channel: channelName
	});

	useEffect(() => {
		if (!query) return;

		if (!salesChannels) return;

		// deconstruct query for SelectInputs values

		const perspectiveIndex = Object.values(statusPerspectives).findIndex(
			(sp) =>
				sp.isStopped == query.isStopped &&
				sp.isExport == query.isExport &&
				sp.isConfirmedExport == query.isConfirmedExport &&
				sp.isReclassifiedExport == query.isReclassifiedExport &&
				((query.status == null && sp.statuses == null) ||
					query.status?.every((qs) => sp.statuses?.includes(qs))),
		);

		const queryPerspective = perspectiveIndex > 0 ? Object.keys(statusPerspectives).at(perspectiveIndex) : "All";

		setPerspective(queryPerspective);

		setChannel(query.channel ? query.channel : "-");
	}, [query, salesChannels]);

	const [exporting, setExporting] = useState(false);
	const triggerExport = async () => {
		if (!query.fullText && perspective === "All") {
			await showMessage({
				title: "Fehler",
				message:
					"Es muss entweder Suchtext eingeben sein oder eine Perspektive ausgewählt sein. Sonst ist der Export zu groß.",
			});
			return;
		}

		setExporting(true);
		try {
			const response = await axios.post(`/api/sales/search`, query, {
				responseType: "blob",
				params: {
					format: "xlsx",
				},
			});

			const filename = parseContentDispositionFilename(response.headers["content-disposition"]);
			if (filename) {
				fileDownload(response.data, filename);
			}
		} finally {
			setExporting(false);
		}
	};

	return (
		<Sidebar>
			<SidebarGroup>
				<SidebarForm>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Perspektive</InputLabel>
						<Select
							value={perspective}
							onChange={(e) => {
								setPerspective(e.target.value);
								setQuery((q) =>
									updateQuery(
										q,
										e.target.value,
										salesChannels?.find((sc) => sc.name === channel)?.name,
									),
								);
							}}
						>
							{Object.keys(statusPerspectives).map((k) => (
								<MenuItem value={k}>{statusPerspectives[k].name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Vertriebskanal</InputLabel>
						<Select
							value={channel}
							onChange={(e) => {
								setChannel(e.target.value);

								const actualChannel = salesChannels.find((c) => c.name === e.target.value)?.name;
								setQuery((q) => updateQuery(q, perspective, actualChannel));
							}}
						>
							<MenuItem value="-">Alle</MenuItem>{" "}
							{/* value here is something what is never proper channel name */}
							{salesChannels && salesChannels.map((sc) => <MenuItem value={sc.name}>{sc.name}</MenuItem>)}
						</Select>
					</FormControl>
				</SidebarForm>
			</SidebarGroup>
			<SidebarGroup>
				<SidebarButton
					startIcon={<GetApp />}
					color="inherit"
					disabled={exporting}
					onClick={triggerExport}
					label="Excel-Export"
				/>
			</SidebarGroup>
			<Grid item>
				<Typography sx={{ opacity: "0.7" }} variant="caption">
					Beim Export sind alle Filter- und Sucheingaben berücksichtigt
				</Typography>
			</Grid>
		</Sidebar>
	);
};

export default SalesTableSidebar;
